import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
	isWritingJobComplete,
	savePreviewEdits,
} from "../../../../services/writingAssistantApi";

/**
 * Manages the lifecycle of user-initiated edits to AI-generated preview
 * changes: per-block drafts, dirty tracking, submission to the backend
 * validation endpoint, and the resulting preview-refresh callback.
 *
 * Mirror of the style used by useWritingJobValidations:
 *  - explicit loading / polling flags
 *  - useCallback for stable handler identities
 *  - useRef for mid-flight cancellation
 *  - toast notifications for high-level outcomes
 */
export function useWritingPreviewEdits(
	websiteId,
	jobId,
	jobStatus,
	changeCount = 0,
	changes = []
) {
	// Map<number, string> – index => current draft markdown (trimmed != base => dirty)
	const [drafts, setDrafts] = useState(new Map());
	// Map<number, string> – snapshot of original new_content_md when edit first started
	const [originalSnapshots, setOriginalSnapshots] = useState(new Map());
	// Set<number> – which indices have been successfully saved in the session
	const [savedIndices, setSavedIndices] = useState(new Set());
	// Map<number, string> – per-edit inline rejection messages from allowed=false responses
	const [rejectionMessages, setRejectionMessages] = useState(new Map());

	const [isSaving, setIsSaving] = useState(false);
	// When saving a single block, holds the change_index being saved (null = not saving / saving-all)
	const [savingIndex, setSavingIndex] = useState(null);
	// Last backend-level rejection explanation (allowed=false) – shown inline on callouts
	const [lastRejectionExplanation, setLastRejectionExplanation] = useState(null);
	// Last 400 / biz-level server message (status=failure or HTTP 400) – inline on callout
	const [lastServerMessage, setLastServerMessage] = useState(null);

	const saveAbortRef = useRef(false);
	const lockedEditIndices = useMemo(() => {
		const set = new Set();
		(changes || []).forEach((change, idx) => {
			const deploymentStatus = String(change?.deployment_status || "").toLowerCase();
			if (change?.allow_edit === false || deploymentStatus === "awaiting_review") {
				set.add(idx);
			}
		});
		return set;
	}, [changes]);

	// Reset everything when the job identity, its completion state, or the
	// number of change blocks materially changes.
	useEffect(() => {
		setDrafts(new Map());
		setOriginalSnapshots(new Map());
		setSavedIndices(new Set());
		setRejectionMessages(new Map());
		setLastRejectionExplanation(null);
		setLastServerMessage(null);
		setIsSaving(false);
		saveAbortRef.current = true;
	}, [jobId, jobStatus, changeCount]);

	// If backend locks a block after deploy, drop local draft state for it.
	useEffect(() => {
		if (lockedEditIndices.size === 0) return;
		setDrafts((prev) => {
			let changed = false;
			const next = new Map(prev);
			lockedEditIndices.forEach((idx) => {
				if (next.delete(idx)) changed = true;
			});
			return changed ? next : prev;
		});
		setSavedIndices((prev) => {
			let changed = false;
			const next = new Set(prev);
			lockedEditIndices.forEach((idx) => {
				if (next.delete(idx)) changed = true;
			});
			return changed ? next : prev;
		});
		setRejectionMessages((prev) => {
			let changed = false;
			const next = new Map(prev);
			lockedEditIndices.forEach((idx) => {
				if (next.delete(idx)) changed = true;
			});
			return changed ? next : prev;
		});
	}, [lockedEditIndices]);

	const getOriginalContent = useCallback(
		(index, fallbackMd) => {
			if (originalSnapshots.has(index)) {
				return originalSnapshots.get(index);
			}
			const md = String(fallbackMd ?? "");
			setOriginalSnapshots((prev) => {
				if (prev.has(index)) return prev;
				const next = new Map(prev);
				next.set(index, md);
				return next;
			});
			return md;
		},
		[originalSnapshots]
	);

	const setDraft = useCallback((index, value, fallbackMd = "") => {
		const safeIndex = Number(index);
		if (!Number.isFinite(safeIndex) || safeIndex < 0) return;
		const nextValue = typeof value === "string" ? value : "";

		// Make sure we have an original snapshot for dirty comparison
		getOriginalContent(safeIndex, fallbackMd);

		setDrafts((prev) => {
			const next = new Map(prev);
			// Keep empty-string as an intentional user draft so deleting all content
			// does not snap back to the original fallback markdown.
			next.set(safeIndex, nextValue);
			return next;
		});

		// Clear any stale inline rejection once the user edits again
		setRejectionMessages((prev) => {
			if (!prev.has(safeIndex)) return prev;
			const next = new Map(prev);
			next.delete(safeIndex);
			return next;
		});
		setSavedIndices((prev) => {
			if (!prev.has(safeIndex)) return prev;
			const next = new Set(prev);
			next.delete(safeIndex);
			return next;
		});
	}, [getOriginalContent]);

	const clearDraft = useCallback((index) => {
		const safeIndex = Number(index);
		setDrafts((prev) => {
			const next = new Map(prev);
			next.delete(safeIndex);
			return next;
		});
		setSavedIndices((prev) => {
			const next = new Set(prev);
			next.delete(safeIndex);
			return next;
		});
		setRejectionMessages((prev) => {
			const next = new Map(prev);
			next.delete(safeIndex);
			return next;
		});
	}, []);

	const clearAllDrafts = useCallback(() => {
		setDrafts(new Map());
		setSavedIndices(new Set());
		setRejectionMessages(new Map());
		setLastRejectionExplanation(null);
		setLastServerMessage(null);
	}, []);

	const getDraftValue = useCallback(
		(index) => {
			const safeIndex = Number(index);
			if (drafts.has(safeIndex)) return drafts.get(safeIndex);
			return undefined;
		},
		[drafts]
	);

	const isDirtyAtIndex = useCallback(
		(index, fallbackMd) => {
			const safeIndex = Number(index);
			const original = originalSnapshots.has(safeIndex)
				? originalSnapshots.get(safeIndex)
				: String(fallbackMd ?? "");
			const current = drafts.has(safeIndex) ? drafts.get(safeIndex) : original;
			return current.trim() !== original.trim();
		},
		[drafts, originalSnapshots]
	);

	const dirtyEdits = useMemo(() => {
		const dirty = [];
		drafts.forEach((content, idxNum) => {
			const index = Number(idxNum);
			const original = originalSnapshots.has(index) ? originalSnapshots.get(index) : "";
			if (content.trim() !== original.trim()) {
				dirty.push({
					change_index: index,
					edited_content: content,
				});
			}
		});
		return dirty;
	}, [drafts, originalSnapshots]);

	const isDirty = useMemo(() => dirtyEdits.length > 0, [dirtyEdits]);
	const dirtyCount = dirtyEdits.length;
	const savedCount = savedIndices.size;

	const getInlineRejection = useCallback(
		(index) => rejectionMessages.get(Number(index)) ?? null,
		[rejectionMessages]
	);

	const isIndexSaved = useCallback(
		(index) => savedIndices.has(Number(index)),
		[savedIndices]
	);

	/**
	 * Internal submit helper shared by submitEdits (all) and submitSingleEdit.
	 */
	const submitEditsInternal = useCallback(
		async (editsToSubmit, { onPreviewRefreshed, singleIndex = null } = {}) => {
			if (websiteId == null || jobId == null) {
				toast.error("Cannot save edits – website or job missing");
				return { ok: false, allowed: false, preview: null };
			}
			if (!isWritingJobComplete(jobStatus)) {
				toast.error("Preview edits are only allowed for completed jobs");
				return { ok: false, allowed: false, preview: null };
			}
			if (isSaving) {
				return { ok: false, allowed: false, preview: null };
			}
			if (!Array.isArray(editsToSubmit) || editsToSubmit.length === 0) {
				toast.info("No changes to save");
				return { ok: false, allowed: false, preview: null };
			}
			const lockedEdits = editsToSubmit.filter((e) =>
				lockedEditIndices.has(Number(e?.change_index))
			);
			if (lockedEdits.length > 0) {
				const lockMessage =
					lockedEdits.length === 1
						? "This change is locked while deployment is in progress."
						: "One or more changes are locked while deployment is in progress.";
				setLastServerMessage(lockMessage);
				setLastRejectionExplanation(lockMessage);
				setRejectionMessages((prev) => {
					const next = new Map(prev);
					lockedEdits.forEach((e) => {
						next.set(Number(e.change_index), lockMessage);
					});
					return next;
				});
				toast.error("Edit not saved");
				return { ok: false, allowed: false, preview: null };
			}

			saveAbortRef.current = false;
			setIsSaving(true);
			setSavingIndex(singleIndex);
			setLastServerMessage(null);
			setLastRejectionExplanation(null);

			try {
				const result = await savePreviewEdits(websiteId, jobId, editsToSubmit);

				if (saveAbortRef.current) {
					return { ok: false, allowed: false, preview: null };
				}

				if (!result.ok) {
					// 400 / biz-level envelope failure – details stay inline; toast is brief
					setLastServerMessage(result.message || "Failed to save edits");
					toast.error("Edit not saved");
					return { ok: false, allowed: false, preview: null };
				}

				if (!result.allowed) {
					// Pipeline rejected edits – explanation inline + keep drafts
					const explanation = result.explanation || "Edits did not pass validation";
					setLastRejectionExplanation(explanation);
					if (typeof explanation === "string") {
						setRejectionMessages(() => {
							const next = new Map();
							editsToSubmit.forEach((e) => next.set(e.change_index, explanation));
							return next;
						});
					}
					toast.error("Edit not saved");
					return {
						ok: true,
						allowed: false,
						explanation,
						preview: null,
					};
				}

				// allowed === true – mark submitted blocks as saved, clear those drafts
				const savedNow = new Set([...savedIndices]);
				const clearedDrafts = new Map(drafts);
				const clearedSnapshots = new Map(originalSnapshots);
				editsToSubmit.forEach((e) => {
					savedNow.add(e.change_index);
					clearedDrafts.delete(e.change_index);
					clearedSnapshots.delete(e.change_index);
				});
				setSavedIndices(savedNow);
				setDrafts(clearedDrafts);
				setOriginalSnapshots(clearedSnapshots);
				setRejectionMessages(new Map());
				setLastRejectionExplanation(null);
				setLastServerMessage(null);

				toast.success("Edit saved");

				if (typeof onPreviewRefreshed === "function" && result.preview) {
					onPreviewRefreshed(result.preview);
				}

				return {
					ok: true,
					allowed: true,
					explanation: null,
					preview: result.preview ?? null,
				};
			} catch {
				if (!saveAbortRef.current) {
					setLastServerMessage("Unexpected error while saving edits");
					toast.error("Edit not saved");
				}
				return { ok: false, allowed: false, preview: null };
			} finally {
				if (!saveAbortRef.current) {
					setIsSaving(false);
					setSavingIndex(null);
				}
			}
		},
		[
			websiteId,
			jobId,
			jobStatus,
			isSaving,
			savedIndices,
			drafts,
			originalSnapshots,
			lockedEditIndices,
		]
	);

	/**
	 * Submit all dirty edits (save-all toolbar button).
	 */
	const submitEdits = useCallback(
		async ({ onPreviewRefreshed } = {}) => {
			return submitEditsInternal(dirtyEdits, { onPreviewRefreshed, singleIndex: null });
		},
		[submitEditsInternal, dirtyEdits]
	);

	/**
	 * Submit only the edit at the given change_index (per-callout Save button).
	 * If the block isn't currently dirty, returns early with ok:false + toast.
	 */
	const submitSingleEdit = useCallback(
		async (changeIndex, { onPreviewRefreshed } = {}) => {
			const safeIndex = Number(changeIndex);
			if (!Number.isFinite(safeIndex) || safeIndex < 0) {
				return { ok: false, allowed: false, preview: null };
			}
			const edit = dirtyEdits.find((e) => e.change_index === safeIndex);
			if (!edit) {
				toast.info("No changes to save on this block");
				return { ok: false, allowed: false, preview: null };
			}
			return submitEditsInternal([edit], { onPreviewRefreshed, singleIndex: safeIndex });
		},
		[submitEditsInternal, dirtyEdits]
	);

	return {
		// Drafts
		drafts,
		getDraftValue,
		setDraft,
		clearDraft,
		clearAllDrafts,
		getOriginalContent,

		// Dirty / saved tracking
		isDirty,
		dirtyCount,
		dirtyEdits,
		isDirtyAtIndex,
		savedCount,
		isIndexSaved,

		// Save state & messages
		isSaving,
		savingIndex,
		lastRejectionExplanation,
		lastServerMessage,
		getInlineRejection,

		// Actions
		submitEdits,
		submitSingleEdit,
	};
}

export default useWritingPreviewEdits;